import { makeAutoObservable, observable } from "mobx";
import Storage from "utils/storage";
import { AppModalTransferProps } from "components/app/modal/transfer";
import { AppModalExChangeProps } from "components/app/modal/equityExChange";
import { AppModalEquityAuthAccountProps } from "components/app/modal/equityAuthAccount";
import { AppModalBorrowRepayProps } from "components/app/modal/borrowRepay";
import { AppModalSubscribeRedeemProps } from "components/app/modal/subscribeRedeem";
import { TypeEnum } from "@/store/market";
import { get_tradeFee } from "@/api/v4/account";

export enum PlateFormEnum {
  USMART = 1,
  FFC = 2,
}

export enum TradeSideEnum { //交易方向
  sell = "SELL",
  buy = "BUY",
}

export enum TradeTypeEnum { //交易类型
  limit = "LIMIT",
  market = "MARKET",
  stopLimit = "ENTRUST_PROFIT",
  trailingStop = "ENTRUST_TRACK",
}

export enum TradeOrderStateEnum { //交易订单状态
  NEW = "NEW", //新建
  FILLED = "FILLED", //完全成交
  PARTIALLY_FILLED = "PARTIALLY_FILLED", //部分成交
  CANCELED = "CANCELED", //用户撤单
  REJECTED = "REJECTED", //下单失败
  EXPIRED = "EXPIRED", //过期(time_in_force撤单或溢价撤单)
  CANCEL_ERROR = "CANCEL_ERROR", // 撤单失败
  NEW_ERROR = "NEW_ERROR", // 报单失败
  PENDING_CANCEL = "PENDING_CANCEL" // 撤单中
}

export enum LayoutAdvancedActiveKeyEnum {
  order = "order", //委托信息
  trade = "trade", //最新成交
}

export enum LayoutH5ActiveKeyEnum {
  chart = "chart", //图表
  order = "order", //委托信息
  trade = "trade", //最新成交
}

//全部市场行情
export interface TickerProps {
  s: string; //市场名称, btc_usdt
  c?: string; //当前价格, 100.000
  lp?: string; //当前价格（新增）, 100.000
  cr?: string; //涨跌幅 0.0582 -> 5.8%
  cv?: string; //价格变动值
  h?: string; //24H最高价 25891.63
  l?: string; //24H最低价 24188.76
  q?: string; //24H成交数量 btc
  v?: string; //24H成交金额 usdt
  displayName?: string; //全称
  codePlate?: string; // 代码市场
}

//最新成交价
export interface TradeRecentProps {
  b: boolean; //是否是买入
  d: number; // 买卖盘色值 1买入 2卖出 0中间色
  i: number; //交易ID(用于精确排序)
  p: string; //成交价
  q: string; //成交量
  t: number; //成交时间毫秒
  v: string; //成交额
}

interface TradeRecentOnceProps extends ObjAny {
  p: string; //成交价
  q: string; //成交量
  total?: string; //累计
  isClick?: boolean; //是否是点击
  isTick?: boolean; // 是否是逐笔行情点击
}

//export type DepthAryProps = Array<Array<string>>; //["27024.70", "3.270777", "100", "300493111417952192"] 价格，数量，累计，nft序列号?
export type DepthAryItemProps = [
  string, //价格，"27024.70"
  string, //数量，"3.270777"
  {
    totalAmount: string; //累计数量，未精度控制
    value: string; //价格*数量，未精度控制，最终产品要求精度固定6位
    totalValue: string; //累计(价格*数量)，未精度控制，最终产品要求精度固定6位
    nft?: string; //nft序列号，"300493111417952192"
  }
];
// 使用简单的string[][]类型，与代码中实际使用一致
export type DepthAryProps = Array<Array<string>>;

// interface LeverWsLiquidationRateProps extends ObjAny {
//   marketName: string; //市场名称
//   liquidationRate: string; //爆仓风险率
// }

export interface SearchHistoryItemProps {
  symbol: string;
  isLever?: boolean;
  tag?: string;
}

interface StateProps {
  tickers: TickerProps[];
  tradeRecent: WithUndefined<TradeRecentProps | null>;
  tradeRecentOnce: WithUndefined<TradeRecentOnceProps | null>;
  depthAsks: WithUndefined<DepthAryProps>;
  depthBids: WithUndefined<DepthAryProps>;
  isMaintainTip: boolean;
  // leverWsLiquidationRate: WithUndefined<LeverWsLiquidationRateProps>;
  //
  modalTransfer: WithUndefined<AppModalTransferProps>;
  modalExChange: WithUndefined<AppModalExChangeProps>;
  modalEquityAuthAccount: WithUndefined<AppModalEquityAuthAccountProps>;
  modalBorrowRepay: WithUndefined<AppModalBorrowRepayProps>;
  modalSubscribeRedeem: WithUndefined<AppModalSubscribeRedeemProps>;
  //
  layoutAdvancedActiveKey: LayoutAdvancedActiveKeyEnum;
  layoutH5ActiveKey: LayoutH5ActiveKeyEnum;
  //
  orderConfirm_limit: boolean;
  orderConfirm_market: boolean;
  orderConfirm_stopLimit: boolean;
  orderConfirm_trailingStop: boolean;
  //
  searchHistory: SearchHistoryItemProps[];
  isDepthShowTotalPrice: boolean;
  totalFrozenFee: number;
  tradeFeeRate: number;
}

const trade = makeAutoObservable(
  {
    tickers: [] as StateProps["tickers"], //全部市场行情
    tradeRecent: undefined as StateProps["tradeRecent"], //最新成交价，实时变动，null表示没有最新成交，undefined表示未赋值
    tradeRecentOnce: undefined as StateProps["tradeRecentOnce"], //最新成交价，首次变动，null表示没有最新成交，undefined表示未赋值
    depthAsks: undefined as StateProps["depthAsks"], //卖盘，从大到小排序，undefined表示初始化未赋值
    depthBids: undefined as StateProps["depthBids"], //买盘，从大到小排序，undefined表示初始化未赋值
    isMaintainTip: false as StateProps["isMaintainTip"], //是否有维护提示语
    // leverWsLiquidationRate: undefined as StateProps["leverWsLiquidationRate"], //杠杆市场ws爆仓风险率
    //modal
    modalTransfer: undefined as StateProps["modalTransfer"], //划转弹窗
    modalExChange: undefined as StateProps["modalExChange"], //兑换弹窗
    modalEquityAuthAccount: undefined as StateProps["modalEquityAuthAccount"], //股票划转前置弹窗认证
    modalBorrowRepay: undefined as StateProps["modalBorrowRepay"], //借款还款对话框
    modalSubscribeRedeem: undefined as StateProps["modalSubscribeRedeem"], //申购赎回对话框
    //layout布局参数
    layoutAdvancedActiveKey: LayoutAdvancedActiveKeyEnum.order as StateProps["layoutAdvancedActiveKey"], //专业版视图激活的tab
    layoutH5ActiveKey: LayoutH5ActiveKeyEnum.chart as StateProps["layoutH5ActiveKey"], //H5视图激活的tab
    //下单确认
    orderConfirm_limit: true as StateProps["orderConfirm_limit"], //限价单确认对话框
    orderConfirm_market: true as StateProps["orderConfirm_market"], //市价单确认对话框
    orderConfirm_stopLimit: true as StateProps["orderConfirm_stopLimit"], //止盈止损确认对话框
    orderConfirm_trailingStop: true as StateProps["orderConfirm_trailingStop"], //跟踪委托确认对话框
    //搜索历史
    searchHistory: [] as StateProps["searchHistory"], //搜索历史
    isDepthShowTotalPrice: false as StateProps["isDepthShowTotalPrice"], //盘口深度是否显示累计价格

    totalFrozenFee: 0 as StateProps["totalFrozenFee"], // 交易手续费配置
    tradeFeeRate: 0 as StateProps["tradeFeeRate"],

    updateState(payload: Partial<StateProps>) {
      for (const va in payload) {
        this[va] = payload[va];
      }
    },
    //
    initOrderConfirm() {
      this.orderConfirm_limit = (() => {
        const orderConfirm_limit = Storage.get("orderConfirm_limit");
        // return orderConfirm_limit !== false;
        return false;
      })();
      this.orderConfirm_market = (() => {
        const orderConfirm_market = Storage.get("orderConfirm_market");
        // return orderConfirm_market !== false;
        return false;
      })();
      this.orderConfirm_stopLimit = (() => {
        const orderConfirm_stopLimit = Storage.get("orderConfirm_stopLimit");
        return orderConfirm_stopLimit !== false;
      })();
      this.orderConfirm_trailingStop = (() => {
        const orderConfirm_trailingStop = Storage.get("orderConfirm_trailingStop");
        return orderConfirm_trailingStop !== false;
      })();
    },
    initSearchHistory() {
      if (this.searchHistory.length) return;
      const ary = Storage.get("searchHistory");
      if (!ary || !ary.length) return;
      this.searchHistory = ary;
    },
    setSearchHistory(doc?: SearchHistoryItemProps) {
      const ary = doc
        ? (() => {
            const searchHistory = [...this.searchHistory];
            const index = searchHistory.findIndex((obj) => obj.symbol === doc.symbol && obj.isLever == doc.isLever);
            if (index >= 0) {
              searchHistory.splice(index, 1);
            }
            return [doc, ...searchHistory].slice(0, 10);
          })()
        : [];
      this.searchHistory = ary;
      Storage.set("searchHistory", ary);
    },
    getTradeFee(config, finallyFun?: () => void) {
      get_tradeFee(config)
        .then((result) => {
          this.updateState({
            totalFrozenFee: +result.totalFrozenFee,
            tradeFeeRate: +result.defaultTradeFeeRate
          });
        })
        .finally(finallyFun);
    },
  },
  {},
  {
    autoBind: true,
    deep: false,
  }
);

export default trade;
